import { useState } from "react";
import { Search, Plus, Users, ChevronsLeft, ChevronsRight, AlertTriangle } from "lucide-react";

const rowClass = (active) =>
  `mb-1 flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-colors duration-200 ${
    active
      ? "bg-[#0052FF]/10 text-[#0052FF] dark:bg-[#0052FF]/20 dark:text-blue-300"
      : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5"
  }`;

export const TeamsListPanel = ({ teams, selectedTeamId, onSelect, totalMembers, onNewTeam, collapsed, onToggleCollapse }) => {
  const [query, setQuery] = useState("");
  const filtered = teams.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()));

  if (collapsed) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-teams-panel-collapsed">
        <button
          type="button"
          onClick={onToggleCollapse}
          title="Expand Teams panel"
          data-testid="org-teams-expand-btn"
          className="rounded-lg p-2 text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <ChevronsRight size={16} />
        </button>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400" title="Teams">
          <Users size={14} />
        </span>
        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">{teams.length}</span>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-teams-panel">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 p-4 dark:border-white/10">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onToggleCollapse}
            title="Collapse Teams panel"
            data-testid="org-teams-collapse-btn"
            className="rounded-lg p-1 text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <ChevronsLeft size={14} />
          </button>
          <h2 className="text-sm font-bold text-slate-950 dark:text-white">Teams</h2>
        </div>
        <button
          type="button"
          onClick={onNewTeam}
          data-testid="org-teams-new-team-btn"
          className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
        >
          <Plus size={13} /> New Team
        </button>
      </div>
      <div className="border-b border-slate-100 p-3 dark:border-white/10">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search teams"
            data-testid="org-teams-search-input"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-8 pr-3 text-xs font-semibold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2" data-testid="org-teams-list-scroll">
        <button type="button" onClick={() => onSelect("all")} data-testid="org-team-item-all" className={rowClass(selectedTeamId === "all")}>
          <span className="flex items-center gap-2">
            <Users size={15} /> All Members
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400">{totalMembers}</span>
        </button>
        {filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-slate-400" data-testid="org-teams-empty-search">No teams found.</p>
        ) : (
          filtered.map((t) => (
            <button key={t.team_id} type="button" onClick={() => onSelect(t.team_id)} data-testid={`org-team-item-${t.team_id}`} className={rowClass(selectedTeamId === t.team_id)}>
              <span className="flex items-center gap-1.5 truncate">
                <span className="truncate">{t.name}</span>
                {!t.has_active_lead && (
                  <AlertTriangle size={11} className="shrink-0 text-amber-500" title="No Team Lead" data-testid={`org-team-item-no-lead-${t.team_id}`} />
                )}
              </span>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500 dark:bg-white/10 dark:text-slate-400">{t.member_count}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
